import {
  ExpandMore,
  ExpandMore as ExpandMoreIcon,
  VisibilityOutlined,
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Checkbox,
  Grid,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { cloneDeep, find } from 'lodash';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { GCFProductsContext } from 'src/components/context';
import { NumberInputWidget } from 'src/components/widget';
import {
  BackSoonAccordion,
  BackSoonAccordionDetails,
  BackSoonAccordionSummary,
} from 'src/components/widget/lineupWidget/editProductPanel/components/backSoon/BackSoon.styled';
import { getDefaultAssetFromGCF } from 'src/components/widget/lineupWidget/editProductPanel/EditProductPanel.helper';
import { IGcfProductList } from 'src/interfaces';
import { lang } from 'src/utils';

import {
  KeyOfOptionalVisualItemsInterface,
  OptionalVisualItemsInterface,
} from '../ListingWithOptionalVisuals.interface';
import { SelectableImageSourceProps } from './SelectableImageSource.interface';
import {
  Container,
  CustomStyledAlert,
  ItemContainer,
} from './SelectableImageSource.styled';

interface SingleImageItem {
  groupKey: string;
  productKey: string;
  sizeKey: string;
  variationKey: string;
  images: OptionalVisualItemsInterface[];
  productCode: string;
}

export const SelectableImageSource = React.forwardRef(
  (props: SelectableImageSourceProps, ref) => {
    const {
      productList,
      visualsLength,
      isDefaultImageSource,
      isDefaultProductUpdated,
      onChange,
      products,
    } = props;

    const { value: gcfProducts, onChangeGCFProductStatus } =
      useContext(GCFProductsContext);

    const [imagesObject, setImagesObject] = useState<SingleImageItem[]>([]);
    const pendingDefaultReset = useRef(false);

    // Marks reset to default (lineup) intent.
    // The actual reset work is handled inside Effect 1 when it next fires due to
    // productList changing after onChange is called
    useEffect(() => {
      if (isDefaultImageSource) {
        pendingDefaultReset.current = true;
      }
    }, [isDefaultImageSource]);

    // Effect 1: Rebuilds imagesObject from productList
    useEffect(() => {
      if (!productList?.groups || !visualsLength) return;

      const groupImages: SingleImageItem[] = [];

      Object.keys(productList.groups).forEach((groupKey) => {
        const group = productList.groups[groupKey];

        group?.products &&
          Object.keys(group.products).forEach((productKey) => {
            const product = group.products[productKey];
            const defaultAssetData = getDefaultAssetFromGCF(
              product?.sizes,
              product.config.defaultSize,
            );
            // sizeKey must not be undefined, and variationKey and images (data) must be present to form a valid item (imageObject)
            if (
              defaultAssetData?.defaultSize === undefined ||
              !defaultAssetData?.defaultFlavor ||
              !defaultAssetData?.data
            ) {
              return;
            }
            groupImages.push({
              groupKey,
              productKey,
              sizeKey: defaultAssetData.defaultSize,
              variationKey: defaultAssetData.defaultFlavor,
              images: defaultAssetData.data,
              productCode: product.productCode,
            });
          });
      });

      // Handles a pending default-reset if one was flagged
      if (pendingDefaultReset.current) {
        pendingDefaultReset.current = false;

        const resetImageObject = groupImages.map((item) => ({
          ...item,
          images: markAllVisible(item.images),
        }));
        let updatedProductList = cloneDeep(productList);
        resetImageObject.forEach((item) => {
          updatedProductList = applyImagesToProductList(
            cloneDeep(item.images),
            updatedProductList,
            item,
          );
        });
        onChange(updatedProductList, true);
        setImagesObject(resetImageObject);
      } else {
        setImagesObject(groupImages);
      }
    }, [productList, visualsLength, onChange, isDefaultImageSource]);

    // Effect 2: Enforces the visualsLength cap on visible images
    useEffect(() => {
      if (
        !visualsLength ||
        imagesObject.length <= visualsLength ||
        isDefaultProductUpdated ||
        !productList
      ) {
        return;
      }

      if (isDefaultImageSource) {
        // Default source: only the first visualsLength items are visible
        let updatedProductList = cloneDeep(productList);

        imagesObject.forEach((item, i) => {
          const clonedImages = cloneDeep(item.images).map((image) => ({
            ...image,
            visible: i < visualsLength,
          }));

          updatedProductList = applyImagesToProductList(
            clonedImages,
            updatedProductList,
            item,
          );
        });

        onChange(updatedProductList, true);
      } else {
        // Selectable source: trim any visible-overflow beyond visualsLength
        const visibleImages = imagesObject.filter((item) =>
          item.images.some((image) => image.visible),
        );

        if (visibleImages.length <= visualsLength) return;

        let updatedProductList = cloneDeep(productList);

        visibleImages.forEach((item, i) => {
          if (i + 1 <= visualsLength) return;

          const clonedImages = cloneDeep(item.images).map((image) => ({
            ...image,
            visible: false,
          }));

          updatedProductList = applyImagesToProductList(
            clonedImages,
            updatedProductList,
            item,
          );
        });

        onChange(updatedProductList, true);
      }
    }, [
      imagesObject,
      visualsLength,
      isDefaultProductUpdated,
      isDefaultImageSource,
      productList,
      onChange,
    ]);

    // Returns a new productList with updated images at the given item's path.
    const applyImagesToProductList = (
      updatedImages: OptionalVisualItemsInterface[],
      updatedProductList: IGcfProductList,
      item: SingleImageItem,
    ): IGcfProductList => {
      updatedProductList.groups[item.groupKey].products[item.productKey].sizes[
        item.sizeKey
      ].variations[item.variationKey].config.images = [...updatedImages];

      return updatedProductList;
    };

    // Clones productList, applies the image update, and commits via onChange.
    const commitImageUpdate = (
      updatedImages: OptionalVisualItemsInterface[],
      item: SingleImageItem,
    ) => {
      if (!productList) return;

      const updatedProductList = applyImagesToProductList(
        updatedImages,
        cloneDeep(productList),
        item,
      );

      onChange(updatedProductList);
    };

    const onChangedField = (
      item: SingleImageItem,
      key: KeyOfOptionalVisualItemsInterface,
      value: string,
    ) => {
      const updatedImage = {
        ...item.images[0],
        style: {
          ...item.images[0].style,
          [key]: value,
        },
      };
      commitImageUpdate([updatedImage], item);
    };

    const onChangedOutageField = (
      item: SingleImageItem,
      key: KeyOfOptionalVisualItemsInterface,
      value: string,
    ) => {
      const updatedImage = {
        ...item.images[0],
        outageStamp: {
          style: {
            ...item.images[0]?.outageStamp?.style,
            [key]: value,
          },
          visible: true,
        },
      };

      commitImageUpdate([updatedImage], item);
    };

    const onCheckChange = (
      item: SingleImageItem,
      checked: boolean,
      index: number,
    ) => {
      const totalVisible = imagesObject.flatMap((obj) =>
        obj.images.filter((image) => image.visible),
      ).length;

      const isUnderCap = totalVisible < (visualsLength ?? 0);

      if (!checked || (checked && isUnderCap)) {
        const updatedImages = item.images.map((image, i) =>
          i === index ? { ...image, visible: checked } : image,
        );
        commitImageUpdate(updatedImages, item);
      }
    };

    // Returns a new array with all images set to visible: true.
    const markAllVisible = (
      images: OptionalVisualItemsInterface[],
    ): OptionalVisualItemsInterface[] =>
      images.map((image) => ({ ...image, visible: true }));

    return (
      <Container ref={ref}>
        {imagesObject.map((item) => {
          const matchGCFProduct = gcfProducts[item.productCode];

          const product = {
            ...find(products, { product_code: item.productCode }),
            ...(matchGCFProduct && { active: matchGCFProduct.active }),
          };

          return item.images.map((image, index) => (
            <Accordion key={`${item.productCode}-${index}`}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1a-content"
                id="panel1a-header"
                style={{ borderBottom: '1px solid #000', padding: 0 }}>
                <Stack alignItems="center" direction="row">
                  <Checkbox
                    checked={image.visible}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) =>
                      onCheckChange(item, e.target.checked, index)
                    }
                    disabled={isDefaultImageSource}
                  />
                  <Typography>{product?.product_name}</Typography>
                </Stack>
              </AccordionSummary>

              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <NumberInputWidget
                      label={lang.width}
                      options={{ suffix: 'px' }}
                      value={image?.style?.width || ''}
                      disabled={!image.visible}
                      onChange={(value) =>
                        onChangedField(item, 'width', value as string)
                      }
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <NumberInputWidget
                      label={lang.height}
                      options={{ suffix: 'px' }}
                      value={image?.style?.height || ''}
                      disabled={!image.visible}
                      onChange={(value) =>
                        onChangedField(item, 'height', value as string)
                      }
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <NumberInputWidget
                      label={lang.xAxis}
                      options={{ suffix: 'px' }}
                      value={image?.style?.left || ''}
                      disabled={!image.visible}
                      onChange={(value) =>
                        onChangedField(item, 'left', value as string)
                      }
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <NumberInputWidget
                      label={lang.yAxis}
                      options={{ suffix: 'px' }}
                      value={image?.style?.top || ''}
                      disabled={!image.visible}
                      onChange={(value) =>
                        onChangedField(item, 'top', value as string)
                      }
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <NumberInputWidget
                      label={lang.zIndex}
                      value={image?.style?.zIndex || ''}
                      disabled={!image.visible}
                      onChange={(value) =>
                        onChangedField(item, 'zIndex', value as string)
                      }
                    />
                  </Grid>
                </Grid>

                <BackSoonAccordion>
                  <BackSoonAccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="h6">{lang.backSoonLabel}</Typography>
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        onChangeGCFProductStatus(
                          product.product_code,
                          !product.active,
                        );
                      }}>
                      <VisibilityOutlined
                        color={product?.active ? 'secondary' : 'primary'}
                      />
                    </IconButton>
                  </BackSoonAccordionSummary>

                  <BackSoonAccordionDetails>
                    <CustomStyledAlert icon={false}>
                      {lang.backSoonText}
                    </CustomStyledAlert>

                    <ItemContainer>
                      <Grid container item xs={12} spacing={2}>
                        <Grid item xs={5}>
                          <NumberInputWidget
                            label={lang.width}
                            options={{ suffix: 'px' }}
                            value={image?.outageStamp?.style?.width || ''}
                            disabled={!image.visible}
                            onChange={(value) =>
                              onChangedOutageField(
                                item,
                                'width',
                                value as string,
                              )
                            }
                          />
                        </Grid>
                        <Grid item xs={5}>
                          <NumberInputWidget
                            label={lang.height}
                            options={{ suffix: 'px' }}
                            value={image?.outageStamp?.style?.height || ''}
                            disabled={!image.visible}
                            onChange={(value) =>
                              onChangedOutageField(
                                item,
                                'height',
                                value as string,
                              )
                            }
                          />
                        </Grid>
                        <Grid item xs={5}>
                          <NumberInputWidget
                            label={lang.leftPosition}
                            options={{ suffix: 'px' }}
                            value={image?.outageStamp?.style?.left || ''}
                            disabled={!image.visible}
                            onChange={(value) =>
                              onChangedOutageField(
                                item,
                                'left',
                                value as string,
                              )
                            }
                          />
                        </Grid>
                        <Grid item xs={5}>
                          <NumberInputWidget
                            label={lang.topPosition}
                            options={{ suffix: 'px' }}
                            value={image?.outageStamp?.style?.top || ''}
                            disabled={!image.visible}
                            onChange={(value) =>
                              onChangedOutageField(item, 'top', value as string)
                            }
                          />
                        </Grid>
                      </Grid>
                    </ItemContainer>
                  </BackSoonAccordionDetails>
                </BackSoonAccordion>
              </AccordionDetails>
            </Accordion>
          ));
        })}
      </Container>
    );
  },
);
